import { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  baseSize: number;
  phase: number;
  speed: number;
  brightness: number;
}

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<Star[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
      initStars();
    };

    const initStars = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Stars concentrate in center area where people sit
      const cx = w * 0.5;
      const cy = h * 0.42;
      const stars: Star[] = [];

      for (let i = 0; i < 45; i++) {
        // Gaussian-like distribution around center
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * Math.random() * 280;
        const x = cx + Math.cos(angle) * radius * 1.4;
        const y = cy + Math.sin(angle) * radius * 0.9;

        stars.push({
          x,
          y,
          baseSize: 1.2 + Math.random() * 2.0,
          phase: Math.random() * Math.PI * 2,
          speed: 0.4 + Math.random() * 1.2,
          brightness: 0.3 + Math.random() * 0.5,
        });
      }
      starsRef.current = stars;
    };

    const draw = (time: number) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);

      const t = time * 0.001;

      starsRef.current.forEach((star) => {
        const twinkle = Math.max(0, Math.sin(star.phase + t * star.speed));
        const intensity = Math.pow(twinkle, 6); // sharp diamond flash
        if (intensity < 0.02) return;

        const size = star.baseSize * (0.6 + intensity * 0.8);
        const alpha = intensity * star.brightness;

        // Warm gold/amber color
        const r = 230 + intensity * 25;
        const g = 195 + intensity * 40;
        const b = 140 + intensity * 30;

        // Diamond core
        ctx.save();
        ctx.translate(star.x, star.y);
        ctx.globalAlpha = alpha;

        // Diamond shape (rotated square)
        ctx.fillStyle = `rgb(${r},${g},${b})`;
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size * 0.55, 0);
        ctx.moveTo(size * 0.55, 0);
        ctx.lineTo(0, size);
        ctx.moveTo(0, size);
        ctx.lineTo(-size * 0.55, 0);
        ctx.moveTo(-size * 0.55, 0);
        ctx.lineTo(0, -size);
        ctx.fill();

        // Cross flare when bright
        if (intensity > 0.4) {
          const flareAlpha = (intensity - 0.4) / 0.6 * alpha * 0.6;
          ctx.globalAlpha = flareAlpha;
          ctx.strokeStyle = `rgb(${r},${g},${b})`;
          ctx.lineWidth = 0.6;

          // Vertical line
          ctx.beginPath();
          ctx.moveTo(0, -size * 3.5);
          ctx.lineTo(0, size * 3.5);
          ctx.stroke();

          // Horizontal line
          ctx.beginPath();
          ctx.moveTo(-size * 2.5, 0);
          ctx.lineTo(size * 2.5, 0);
          ctx.stroke();

          // Outer glow dot
          ctx.globalAlpha = flareAlpha * 0.3;
          ctx.fillStyle = `rgb(${r},${g},${b})`;
          ctx.beginPath();
          ctx.arc(0, 0, size * 0.8, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full overflow-hidden"
      style={{
        height: '100vh',
        background: 'linear-gradient(175deg, #e8d8c4 0%, #ede0cc 15%, #f0e6d4 40%, #f3ebdc 60%, #ede0cc 85%, #e8d8c4 100%)',
      }}
    >
      {/* Workshop photo - faint warm silhouette */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/images/hero-workshop.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          opacity: 0.07,
          filter: 'sepia(0.35) saturate(0.65) contrast(0.8) brightness(1.15)',
          zIndex: 1,
        }}
      />
      {/* Edge fade mask for photo */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 42%, transparent 20%, rgba(235, 228, 214, 0.5) 70%, rgba(235, 228, 214, 0.85) 100%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Warm center glow - like golden hour light */}
      <div
        className="absolute"
        style={{
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70vw',
          height: '55vh',
          background: 'radial-gradient(ellipse at center, rgba(225, 200, 160, 0.18) 0%, rgba(210, 180, 140, 0.06) 40%, transparent 70%)',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      />

      {/* Soft warm haze overlay for atmosphere */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 90% 80% at 50% 40%, transparent 30%, rgba(237, 231, 219, 0.5) 100%)',
          zIndex: 4,
          pointerEvents: 'none',
        }}
      />

      {/* Diamond sparkle canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />

      {/* Content - dead center of page, refined scale */}
      <div
        className="relative z-10 flex flex-col justify-center items-center h-full px-6 lg:px-12"
        style={{ pointerEvents: 'none' }}
      >
        <div className="max-w-2xl text-center" style={{ pointerEvents: 'auto' }}>
          <p
            className="text-caption mb-4"
            style={{ fontSize: '0.72rem', color: '#a08e78', letterSpacing: '0.12em' }}
          >
            孔隙心理 — 社会心理健康服务生态资源运营平台
          </p>

          <h1
            className="font-body"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.6rem)',
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: '0.02em',
              color: '#2c2420',
              wordBreak: 'keep-all',
              textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.25), 3px 4px 8px rgba(180,160,130,0.15)',
            }}
          >
            心理学深度
          </h1>
          <p
            style={{
              fontSize: 'clamp(1.3rem, 2.8vw, 2.2rem)',
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              color: '#9a8a78',
              marginTop: '2px',
              textShadow: '1px 1px 0 #d4c4b0, 2px 2px 0 rgba(180,160,130,0.2), 3px 4px 8px rgba(180,160,130,0.12)',
            }}
          >
            × AI效率
          </p>

          <div className="mt-5 max-w-lg mx-auto">
            <p
              style={{
                fontSize: '0.9rem',
                fontWeight: 600,
                lineHeight: 1.4,
                color: '#5a4a3a',
                letterSpacing: '0.08em',
                textShadow: '0 1px 10px rgba(240, 230, 212, 0.9)',
              }}
            >
              四层递进，人智协同
            </p>
            <p
              className="mt-2"
              style={{
                fontSize: '0.78rem',
                lineHeight: 1.6,
                color: '#8a7a68',
                letterSpacing: '0.03em',
                textShadow: '0 1px 10px rgba(240, 230, 212, 0.9)',
              }}
            >
              AI引擎发现异常 <span style={{ color: '#E8A838' }}>-</span> AI+游戏化精准诊断 <span style={{ color: '#E8A838' }}>-</span> 主题工作坊精准突破 <span style={{ color: '#E8A838' }}>-</span> 情绪热线持续护航
            </p>
          </div>

          <div className="flex gap-3 mt-8 justify-center">
            <button
              className="interactive px-6 py-2.5 text-caption transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: '#2c2420',
                color: '#f7f2e9',
                borderRadius: '9999px',
                letterSpacing: '0.1em',
                fontSize: '0.8rem',
              }}
              onClick={() => {
                const el = document.getElementById('showcase');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              了解服务
            </button>
            <button
              className="interactive px-6 py-2.5 text-caption transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: 'transparent',
                color: '#2c2420',
                border: '1px solid rgba(44, 36, 32, 0.2)',
                borderRadius: '9999px',
                letterSpacing: '0.1em',
                fontSize: '0.8rem',
              }}
              onClick={() => {
                const el = document.getElementById('ai-chat');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              AI体验
            </button>
          </div>

        </div>
      </div>

      {/* Bottom warm transition line */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '1px',
          background: 'linear-gradient(90deg, transparent 5%, rgba(160, 142, 120, 0.25) 50%, transparent 95%)',
          zIndex: 10,
        }}
      />

      {/* Designer quote - bottom right, 3D carved calligraphy */}
      <div
        className="absolute z-10 hidden lg:flex"
        style={{
          bottom: '7%',
          right: '3.5%',
          pointerEvents: 'none',
          gap: '16px',
          alignItems: 'flex-start',
        }}
      >
        {/* Left column: 每一种触感 */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
          {['每', '一', '种', '触', '感'].map((char, i) => (
            <span key={`l${i}`} style={{
              fontFamily: "'KaiTi', 'STKaiti', 'Noto Serif SC', 'FangSong', serif",
              fontSize: '1.05rem',
              fontWeight: 500,
              fontStyle: 'italic',
              color: 'rgba(90, 72, 52, 0.85)',
              lineHeight: 1,
              letterSpacing: '0.08em',
              animation: `carvedFloat 5s ease-in-out ${i * 0.2}s infinite`,
              textShadow: `
                -1px -1px 0 rgba(220, 200, 175, 0.7),
                1px 1px 0 rgba(50, 35, 20, 0.5),
                2px 2px 3px rgba(50, 35, 20, 0.3),
                3px 4px 8px rgba(50, 35, 20, 0.15),
                0 0 12px rgba(200, 180, 155, 0.1)
              `,
            }}>{char}</span>
          ))}
        </div>

        {/* Middle column: 都在寻找它 - offset down */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', paddingTop: '28px' }}>
          {['都', '在', '寻', '找', '它'].map((char, i) => (
            <span key={`m${i}`} style={{
              fontFamily: "'KaiTi', 'STKaiti', 'Noto Serif SC', 'FangSong', serif",
              fontSize: '1.05rem',
              fontWeight: 500,
              fontStyle: 'italic',
              color: 'rgba(90, 72, 52, 0.85)',
              lineHeight: 1,
              letterSpacing: '0.08em',
              animation: `carvedFloat 5s ease-in-out ${0.4 + i * 0.2}s infinite`,
              textShadow: `
                -1px -1px 0 rgba(220, 200, 175, 0.7),
                1px 1px 0 rgba(50, 35, 20, 0.5),
                2px 2px 3px rgba(50, 35, 20, 0.3),
                3px 4px 8px rgba(50, 35, 20, 0.15),
                0 0 12px rgba(200, 180, 155, 0.1)
              `,
            }}>{char}</span>
          ))}
        </div>

        {/* Right column: 的手指 - offset middle */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', paddingTop: '14px' }}>
          {['的', '手', '指'].map((char, i) => (
            <span key={`r${i}`} style={{
              fontFamily: "'KaiTi', 'STKaiti', 'Noto Serif SC', 'FangSong', serif",
              fontSize: '1.05rem',
              fontWeight: 500,
              fontStyle: 'italic',
              color: 'rgba(90, 72, 52, 0.85)',
              lineHeight: 1,
              letterSpacing: '0.08em',
              animation: `carvedFloat 5s ease-in-out ${0.8 + i * 0.2}s infinite`,
              textShadow: `
                -1px -1px 0 rgba(220, 200, 175, 0.7),
                1px 1px 0 rgba(50, 35, 20, 0.5),
                2px 2px 3px rgba(50, 35, 20, 0.3),
                3px 4px 8px rgba(50, 35, 20, 0.15),
                0 0 12px rgba(200, 180, 155, 0.1)
              `,
            }}>{char}</span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes charBob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes carvedFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          33% { transform: translateY(-4px) scale(1.02); }
          66% { transform: translateY(-2px) scale(0.99); }
        }
      `}</style>
    </section>
  );
}
